/**
 * Tutorial Quiz Component
 * Provides interactive quizzes after completing each quest
 */

import React, { useState, useMemo, useCallback } from "react";
import PropTypes from "prop-types";
import { Button, Typography, Space, Progress, Radio, Tag } from "antd";
import {
  CheckCircleFilled,
  CloseCircleFilled,
  TrophyOutlined,
  RightOutlined,
  ReloadOutlined,
  ArrowLeftOutlined,
  BookOutlined
} from "@ant-design/icons";
import { getQuizForUserTypeAndQuest, calculateQuizResult } from "./UserTypeQuizData";
import styles from "./TutorialQuests.module.css";

const { Title, Text, Paragraph } = Typography;

/**
 * Congratulations SVG Animation
 */
const CongratsSvg = ({ passed }) => (
  <svg width="120" height="120" viewBox="0 0 120 120" style={{ margin: '0 auto', display: 'block' }}>
    {passed ? (
      <>
        {/* Trophy */}
        <defs>
          <linearGradient id="goldGradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#ffd700" />
            <stop offset="50%" stopColor="#ffec8b" />
            <stop offset="100%" stopColor="#ffd700" />
          </linearGradient>
        </defs>
        {/* Trophy cup */}
        <path
          d="M40 30 L80 30 L75 60 L70 65 L70 75 L75 80 L45 80 L50 75 L50 65 L45 60 Z"
          fill="url(#goldGradient)"
          stroke="#b8860b"
          strokeWidth="2"
        />
        {/* Trophy handles */}
        <ellipse cx="32" cy="42" rx="8" ry="12" fill="none" stroke="url(#goldGradient)" strokeWidth="4" />
        <ellipse cx="88" cy="42" rx="8" ry="12" fill="none" stroke="url(#goldGradient)" strokeWidth="4" />
        {/* Base */}
        <rect x="42" y="80" width="36" height="8" rx="2" fill="url(#goldGradient)" stroke="#b8860b" strokeWidth="1" />
        <rect x="38" y="88" width="44" height="6" rx="2" fill="#b8860b" />
        {/* Star */}
        <polygon
          points="60,35 63,44 73,44 65,50 68,60 60,54 52,60 55,50 47,44 57,44"
          fill="#fff"
          opacity="0.9"
        />
        {/* Sparkles */}
        <circle cx="25" cy="25" r="3" fill="#ffd700" className={styles.sparkle1} />
        <circle cx="95" cy="20" r="2" fill="#ffd700" className={styles.sparkle2} />
        <circle cx="100" cy="55" r="2.5" fill="#ffd700" className={styles.sparkle3} />
        <circle cx="20" cy="60" r="2" fill="#ffd700" className={styles.sparkle2} />
      </>
    ) : (
      <>
        {/* Book for try again */}
        <rect x="30" y="25" width="60" height="70" rx="4" fill="#1890ff" />
        <rect x="35" y="30" width="50" height="60" rx="2" fill="#fff" />
        <line x1="60" y1="30" x2="60" y2="90" stroke="#e8e8e8" strokeWidth="2" />
        {/* Pages */}
        <line x1="40" y1="45" x2="55" y2="45" stroke="#d9d9d9" strokeWidth="2" />
        <line x1="40" y1="55" x2="55" y2="55" stroke="#d9d9d9" strokeWidth="2" />
        <line x1="40" y1="65" x2="55" y2="65" stroke="#d9d9d9" strokeWidth="2" />
        <line x1="65" y1="45" x2="80" y2="45" stroke="#d9d9d9" strokeWidth="2" />
        <line x1="65" y1="55" x2="80" y2="55" stroke="#d9d9d9" strokeWidth="2" />
        <line x1="65" y1="65" x2="80" y2="65" stroke="#d9d9d9" strokeWidth="2" />
        {/* Lightbulb */}
        <circle cx="60" cy="15" r="10" fill="#faad14" />
        <rect x="57" y="23" width="6" height="4" fill="#d48806" />
        <line x1="60" y1="2" x2="60" y2="6" stroke="#faad14" strokeWidth="2" />
        <line x1="48" y1="8" x2="51" y2="11" stroke="#faad14" strokeWidth="2" />
        <line x1="72" y1="8" x2="69" y2="11" stroke="#faad14" strokeWidth="2" />
      </>
    )}
  </svg>
);

/**
 * Score Display Component
 */
const ScoreDisplay = ({ score, passed, correctCount, totalQuestions }) => (
  <div className={styles.scoreDisplay}>
    <div className={styles.scoreCircle} style={{
      borderColor: passed ? '#52c41a' : '#faad14',
      background: passed 
        ? 'linear-gradient(135deg, #f6ffed 0%, #d9f7be 100%)'
        : 'linear-gradient(135deg, #fffbe6 0%, #fff1b8 100%)'
    }}>
      <span className={styles.scoreValue} style={{ color: passed ? '#52c41a' : '#faad14' }}>
        {score}%
      </span>
      <span className={styles.scoreLabel}>Score</span>
    </div>
    <div className={styles.scoreDetails}>
      <Text>
        {correctCount} of {totalQuestions} correct
      </Text>
      <Tag color={passed ? 'success' : 'warning'} style={{ marginTop: 8 }}>
        {passed ? '✓ PASSED' : 'Try Again'}
      </Tag>
    </div>
  </div>
);

/**
 * Question Component
 */
const QuestionCard = ({ question, questionIndex, selectedAnswer, onSelectAnswer, showResult, result }) => {
  const isCorrect = result?.isCorrect;

  return (
    <div className={styles.questionCard}>
      <div className={styles.questionHeader}>
        <Tag color="blue">Question {questionIndex + 1}</Tag>
      </div>
      <Paragraph className={styles.questionText}>
        {question.question}
      </Paragraph>
      <Radio.Group
        onChange={(e) => onSelectAnswer(questionIndex, e.target.value)}
        value={selectedAnswer}
        disabled={showResult}
        className={styles.optionsGroup}
      >
        <Space direction="vertical" style={{ width: '100%' }}>
          {question.options.map((option, optIndex) => {
            let optionClass = styles.optionItem;
            if (showResult) {
              if (optIndex === question.correctIndex) {
                optionClass += ` ${styles.correctOption}`;
              } else if (optIndex === selectedAnswer && !isCorrect) {
                optionClass += ` ${styles.incorrectOption}`;
              }
            }
            return (
              <Radio key={optIndex} value={optIndex} className={optionClass}>
                {option}
              </Radio>
            );
          })}
        </Space>
      </Radio.Group>
      
      {showResult && (
        <div className={`${styles.explanationBox} ${isCorrect ? styles.correctExplanation : styles.incorrectExplanation}`}>
          <Space align="start">
            {isCorrect ? (
              <CheckCircleFilled style={{ color: '#52c41a', fontSize: 16, marginTop: 2 }} />
            ) : (
              <CloseCircleFilled style={{ color: '#ff4d4f', fontSize: 16, marginTop: 2 }} />
            )}
            <div>
              <Text strong style={{ color: isCorrect ? '#52c41a' : '#ff4d4f' }}>
                {isCorrect ? 'Correct!' : 'Incorrect'}
              </Text>
              <Paragraph style={{ margin: '4px 0 0 0', fontSize: 13 }}>
                {question.explanation}
              </Paragraph>
            </div>
          </Space>
        </div>
      )}
    </div>
  );
};

/**
 * Main Quiz Modal/Panel Component
 */
export const TutorialQuiz = ({
  questId,
  questTitle,
  isOpen,
  onClose,
  onRetakeQuiz,
  onBackToQuest,
  userType = 'installer'
}) => {
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [showResults, setShowResults] = useState(false);
  const [quizResult, setQuizResult] = useState(null);

  const quiz = useMemo(() => getQuizForUserTypeAndQuest(userType, questId), [userType, questId]);

  const handleSelectAnswer = useCallback((questionIndex, answer) => {
    setAnswers(prev => ({
      ...prev,
      [questionIndex]: answer
    }));
  }, []);

  const handleNext = useCallback(() => {
    if (currentQuestionIndex < quiz.questions.length - 1) {
      setCurrentQuestionIndex(prev => prev + 1);
    }
  }, [currentQuestionIndex, quiz]);

  const handlePrevious = useCallback(() => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(prev => prev - 1);
    }
  }, [currentQuestionIndex]);

  const handleSubmit = useCallback(() => {
    const result = calculateQuizResult(questId, answers);
    setQuizResult(result);
    setShowResults(true);
  }, [questId, answers]);

  const handleRetake = useCallback(() => {
    setAnswers({});
    setCurrentQuestionIndex(0);
    setShowResults(false);
    setQuizResult(null);
    if (onRetakeQuiz) onRetakeQuiz();
  }, [onRetakeQuiz]);

  const answeredCount = Object.keys(answers).length;
  const progressPercent = quiz ? Math.round((answeredCount / quiz.questions.length) * 100) : 0;
  const allAnswered = quiz ? answeredCount === quiz.questions.length : false;

  if (!isOpen || !quiz) return null;

  const currentQuestion = quiz.questions[currentQuestionIndex];

  return (
    <div className={styles.quizPanel}>
      {/* Header */}
      <div className={styles.quizHeader}>
        <div className={styles.quizTitleRow}>
          <Space>
            <BookOutlined style={{ color: '#1890ff' }} />
            <Text strong style={{ fontSize: 16 }}>{quiz.title}</Text>
          </Space>
          <Button
            type="text"
            size="small"
            icon={<ArrowLeftOutlined />}
            onClick={onBackToQuest}
          >
            Back
          </Button>
        </div>
        <Progress
          percent={showResults ? 100 : progressPercent}
          size="small"
          strokeColor={showResults 
            ? (quizResult?.passed ? '#52c41a' : '#faad14')
            : { '0%': '#1890ff', '100%': '#52c41a' }
          }
          style={{ marginTop: 8 }}
        />
        {!showResults && (
          <Text type="secondary" style={{ fontSize: 11 }}>
            {answeredCount} of {quiz.questions.length} answered • Pass: {quiz.passingScore}%
          </Text>
        )}
      </div>

      {/* Content */}
      <div className={styles.quizContent}>
        {showResults ? (
          // Results View
          <div className={styles.resultsContainer}>
            <CongratsSvg passed={quizResult.passed} />
            
            <Title level={3} style={{ 
              textAlign: 'center', 
              marginTop: 16,
              color: quizResult.passed ? '#52c41a' : '#faad14'
            }}>
              {quizResult.passed 
                ? '🎉 Congratulations!' 
                : '📚 Keep Learning!'}
            </Title>
            
            <Paragraph style={{ textAlign: 'center', color: '#595959' }}>
              {quizResult.passed 
                ? `Excellent work! You've mastered the concepts in "${questTitle}".`
                : `You need ${quiz.passingScore}% to pass. Review the material and try again!`}
            </Paragraph>

            <ScoreDisplay 
              score={quizResult.score}
              passed={quizResult.passed}
              correctCount={quizResult.correctCount}
              totalQuestions={quizResult.totalQuestions}
            />

            {/* Question Review */}
            <div className={styles.reviewSection}>
              <Title level={5}>
                <BookOutlined style={{ marginRight: 8 }} />
                Review Your Answers
              </Title>
              {quiz.questions.map((question, idx) => (
                <QuestionCard
                  key={question.id}
                  question={question}
                  questionIndex={idx}
                  selectedAnswer={answers[idx]}
                  showResult={true}
                  result={quizResult.results[idx]}
                  onSelectAnswer={() => {}}
                />
              ))}
            </div>
          </div>
        ) : (
          // Question View
          <QuestionCard
            question={currentQuestion}
            questionIndex={currentQuestionIndex}
            selectedAnswer={answers[currentQuestionIndex]}
            showResult={false}
            onSelectAnswer={handleSelectAnswer}
          />
        )}
      </div>

      {/* Footer */}
      <div className={styles.quizFooter}>
        {showResults ? (
          <Space>
            <Button onClick={onClose}>
              Close
            </Button>
            <Button
              type="primary"
              icon={<ReloadOutlined />}
              onClick={handleRetake}
            >
              Retake Quiz
            </Button>
          </Space>
        ) : (
          <>
            <Button
              disabled={currentQuestionIndex === 0}
              onClick={handlePrevious}
            >
              Previous
            </Button>
            <Space>
              {currentQuestionIndex < quiz.questions.length - 1 ? (
                <Button
                  type="primary"
                  onClick={handleNext}
                  icon={<RightOutlined />}
                  iconPosition="end"
                  disabled={answers[currentQuestionIndex] === undefined}
                >
                  Next
                </Button>
              ) : (
                <Button
                  type="primary"
                  onClick={handleSubmit}
                  icon={<TrophyOutlined />}
                  disabled={!allAnswered}
                >
                  Submit Quiz
                </Button>
              )}
            </Space>
          </>
        )}
      </div>
    </div>
  );
};

TutorialQuiz.propTypes = {
  questId: PropTypes.number.isRequired,
  questTitle: PropTypes.string.isRequired,
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onRetakeQuiz: PropTypes.func,
  onBackToQuest: PropTypes.func.isRequired,
  userType: PropTypes.oneOf(['installer', 'energyConsultant', 'salesPerson'])
};

export default TutorialQuiz;
